import { supabase } from "@/integrations/supabase/client";
import { University } from "@/services/universityService";

export interface PendingUniversity {
  id: string;
  name: string;
  address: string | null;
  city: string;
  postcode: string | null;
  country: string;
  submitted_by: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  // Joined data
  submitter_email?: string;
  submitter_name?: string;
}

export async function fetchPendingUniversities(): Promise<PendingUniversity[]> {
  try {
    const { data, error } = await supabase
      .from('pending_universities')
      .select(`
        *,
        profiles:submitted_by(username, display_username)
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching pending universities:', error);
      return [];
    }

    return (data || []).map((item: any) => ({
      ...item,
      status: item.status as 'pending' | 'approved' | 'rejected',
      submitter_email: item.profiles?.username,
      submitter_name: item.profiles?.display_username || item.profiles?.username,
    }));
  } catch (error) {
    console.error('Exception fetching pending universities:', error);
    return [];
  }
}

export async function submitPendingUniversity(
  university: Omit<PendingUniversity, 'id' | 'status' | 'admin_notes' | 'created_at' | 'reviewed_at' | 'reviewed_by' | 'submitted_by'>
): Promise<PendingUniversity | null> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      console.error('No authenticated user');
      return null;
    }

    const { data, error } = await supabase
      .from('pending_universities')
      .insert([{
        ...university,
        submitted_by: userData.user.id,
        status: 'pending'
      }])
      .select()
      .single();

    if (error) {
      console.error('Error submitting pending university:', error);
      return null;
    }

    return {
      ...data,
      status: data.status as 'pending' | 'approved' | 'rejected'
    };
  } catch (error) {
    console.error('Exception submitting pending university:', error);
    return null;
  }
}

export async function approveUniversity(pendingUniversity: PendingUniversity): Promise<University | null> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      console.error('No authenticated user');
      return null;
    }

    // Insert into universities table
    const { data: newUniversity, error: insertError } = await supabase
      .from('universities')
      .insert([{
        name: pendingUniversity.name,
        address: pendingUniversity.address,
        city: pendingUniversity.city,
        postcode: pendingUniversity.postcode,
        country: pendingUniversity.country,
        is_approved: true,
        approved_at: new Date().toISOString(),
        approved_by: userData.user.id
      }])
      .select()
      .single();

    if (insertError) {
      console.error('Error inserting university:', insertError);
      return null;
    }

    // Update pending university status
    const { error: updateError } = await supabase
      .from('pending_universities')
      .update({
        status: 'approved',
        reviewed_at: new Date().toISOString(),
        reviewed_by: userData.user.id
      })
      .eq('id', pendingUniversity.id);

    if (updateError) {
      console.error('Error updating pending university status:', updateError);
    }

    return newUniversity;
  } catch (error) {
    console.error('Exception approving university:', error);
    return null;
  }
}

export async function rejectUniversity(id: string, adminNotes?: string): Promise<boolean> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      console.error('No authenticated user');
      return false;
    }

    const { error } = await supabase
      .from('pending_universities')
      .update({
        status: 'rejected',
        admin_notes: adminNotes,
        reviewed_at: new Date().toISOString(),
        reviewed_by: userData.user.id
      })
      .eq('id', id);

    if (error) {
      console.error('Error rejecting university:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Exception rejecting university:', error);
    return false;
  }
}
